import { useState } from "react";
import { motion } from "framer-motion";
import { Mail, Phone, MapPin, Instagram, Youtube, Music2, Loader2, CheckCircle2 } from "lucide-react";
import { PageWrap } from "@/components/PageWrap";
import { SectionHeading } from "@/components/SectionHeading";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { postContactMessage } from "@/lib/api";
import { useLang } from "@/i18n/LanguageContext";

export default function Contact() {
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const { t } = useLang();

  const INFO = [
    { icon: Mail, label: t("contact.labelEmail"), value: "f.vimpel@seznam.cz" },
    { icon: Phone, label: t("contact.labelPhone"), value: "+420 603 380 190" },
    { icon: MapPin, label: t("contact.labelWorkshop"), value: t("contact.workshopValue") },
  ];

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error(t("contact.errRequired"));
      return;
    }
    setSubmitting(true);
    try {
      await postContactMessage(form);
      setSuccess(true);
      toast.success(t("contact.success"));
    } catch {
      toast.error(t("contact.errSubmit"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <PageWrap testId="contact-page">
      <section className="carbon-bg relative overflow-hidden pt-36 pb-12">
        <div className="speed-lines absolute inset-0" />
        <div className="relative z-10 mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.1 }}>
            <div className="font-mono text-xs uppercase tracking-[0.3em] text-[#B7FF2A]">// {t("contact.kicker")}</div>
            <h1 data-testid="contact-headline" className="mt-2 font-display text-6xl uppercase leading-[0.9] tracking-[0.03em] sm:text-8xl">
              {t("contact.title")}
            </h1>
          </motion.div>
        </div>
      </section>

      <section className="mx-auto max-w-6xl px-4 py-16 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-12">
          {/* info */}
          <div className="lg:col-span-5">
            <SectionHeading kicker={t("contact.infoKicker")} title={t("contact.infoTitle")} testId="contact-info-heading" />
            <div className="mt-8 space-y-3">
              {INFO.map((i, idx) => (
                <motion.div
                  key={i.label}
                  initial={{ opacity: 0, x: -24 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.45, delay: 0.08 * idx }}
                  className="clip-corner-sm flex items-center gap-4 border border-white/10 bg-[#0F1217] p-4"
                >
                  <div className="clip-corner-sm flex h-11 w-11 shrink-0 items-center justify-center bg-[#1EA7FF]/10 text-[#1EA7FF]">
                    <i.icon className="h-5 w-5" />
                  </div>
                  <div>
                    <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/40">{i.label}</div>
                    <div className="mt-0.5 text-sm text-white/85">{i.value}</div>
                  </div>
                </motion.div>
              ))}
            </div>
            <div className="mt-6 flex items-center gap-2">
              {[
                { icon: Instagram, href: "https://www.instagram.com/driftex_/?hl=cs", id: "instagram" },
                { icon: Youtube, href: "https://www.youtube.com/@Driftex606", id: "youtube" },
                { icon: Music2, href: "https://www.tiktok.com/@filipvimpel", id: "tiktok" },
              ].map((s) => (
                <a
                  key={s.id}
                  href={s.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`contact-social-${s.id}`}
                  className="flex h-11 w-11 items-center justify-center rounded-md border border-white/10 text-white/60 transition-colors duration-200 hover:border-[#B7FF2A]/50 hover:text-[#B7FF2A]"
                  aria-label={s.id}
                >
                  <s.icon className="h-5 w-5" />
                </a>
              ))}
            </div>
          </div>

          {/* form */}
          <div className="lg:col-span-7">
            {success ? (
              <motion.div
                data-testid="contact-success"
                initial={{ opacity: 0, scale: 0.96 }}
                animate={{ opacity: 1, scale: 1 }}
                className="clip-corner flex h-full flex-col items-center justify-center gap-4 border border-[#B7FF2A]/30 bg-[#0F1217] p-10 text-center"
              >
                <div className="flex h-16 w-16 items-center justify-center rounded-full bg-[#B7FF2A]/10">
                  <CheckCircle2 className="h-8 w-8 text-[#B7FF2A]" />
                </div>
                <div className="font-display text-3xl uppercase tracking-[0.06em]">{t("contact.successTitle")}</div>
                <p className="max-w-sm text-sm leading-relaxed text-white/55">{t("contact.successText")}</p>
                <Button
                  data-testid="contact-new-message-button"
                  variant="outline"
                  onClick={() => {
                    setSuccess(false);
                    setForm({ name: "", email: "", message: "" });
                  }}
                  className="clip-corner-sm border-white/20 font-display uppercase tracking-[0.14em] hover:bg-white/5"
                >
                  {t("contact.sendAnother")}
                </Button>
              </motion.div>
            ) : (
              <form
                data-testid="contact-form"
                onSubmit={submit}
                className="clip-corner space-y-4 border border-white/10 bg-[#0F1217] p-6 sm:p-8"
              >
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <Input data-testid="contact-name-input" placeholder={t("contact.phName")} value={form.name} onChange={set("name")} className="h-11 border-white/15 bg-[#0B0D10]" />
                  <Input data-testid="contact-email-input" type="email" placeholder={t("contact.phEmail")} value={form.email} onChange={set("email")} className="h-11 border-white/15 bg-[#0B0D10]" />
                </div>
                <Textarea
                  data-testid="contact-message-textarea"
                  placeholder={t("contact.phMessage")}
                  value={form.message}
                  onChange={set("message")}
                  className="min-h-40 border-white/15 bg-[#0B0D10]"
                />
                <Button
                  type="submit"
                  disabled={submitting}
                  data-testid="contact-submit-button"
                  className="light-sweep clip-corner-sm h-12 w-full bg-[#1EA7FF] font-display text-base uppercase tracking-[0.14em] text-[#07080A] hover:bg-[#1EA7FF] hover:brightness-110 disabled:opacity-60"
                >
                  {submitting ? (
                    <span className="flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> {t("contact.sending")}</span>
                  ) : (
                    t("contact.submit")
                  )}
                </Button>
              </form>
            )}
          </div>
        </div>
      </section>
    </PageWrap>
  );
}
